import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { format, differenceInCalendarDays, isValid } from "date-fns";
import { customGetAPI } from "services/utils/CustomAPI";
import { useFetch } from "services/hooks/useFetch";
import { stripEmployeeIdFromName } from "pages/TeamZone/Approve/displayNameUtils";

// Leave years come back as "dd-MM-yyyy"; leave_requests dates can be either
// "dd-MM-yyyy" or "yyyy-MM-dd" depending on the row, so parse tolerantly and
// return null (never an Invalid Date) when a value doesn't match.
export const parseDate = (value) => {
  if (!value) return null;
  const dmy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
  if (dmy) return new Date(+dmy[3], +dmy[2] - 1, +dmy[1]);
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (ymd) return new Date(+ymd[1], +ymd[2] - 1, +ymd[3]);
  const fallback = new Date(value);
  return isValid(fallback) ? fallback : null;
};

// leavesummary wants yyyy-MM-dd; leave years arrive as dd-MM-yyyy.
export const toApiDate = (ddmmyyyy) =>
  ddmmyyyy ? ddmmyyyy.split("-").reverse().join("-") : "";

// All data + derived values for the "Leave Details" view, shared by the
// full-page LeaveDetailsScreen and the right-hand LeaveDetailsDrawer so both
// read from a single source. It relies on the same three endpoints the
// redesigned Leave screen uses and invents no new ones:
//   - leaves-years ....... available leave years (the date-range selector)
//   - leavetypefilter .... per-type credited/used -> the balance cards + totals
//   - leavesummary ....... this year's leave_requests -> history + identity
// Optional `yearState` ([selectedYear, setSelectedYear]) lets a caller own the
// selected year so it survives this hook's host remounting (e.g. the side
// panel's expand/collapse swaps its content into a new portal tree).
export const useLeaveDetailsData = (yearState) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, userDetails, token } = useSelector(
    (state) => state.tokenReducer,
  );

  const { result: leaveYears } = useFetch(`${domain}leaves-years/`);
  const localYearState = useState(null);
  const [selectedYear, setSelectedYear] = yearState || localYearState;
  const [leaveCount, setLeaveCount] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Default to the first (current) leave year once the list loads.
  useEffect(() => {
    if (leaveYears?.length && !selectedYear) {
      setSelectedYear(leaveYears[0]);
    }
  }, [leaveYears, selectedYear]);

  // Per-type balances (credited / used) for the selected year -- refetches on
  // year change (selectedYear was missing from this effect's deps, so the
  // year picker never actually refreshed these numbers before).
  useEffect(() => {
    const fetchLeaveTypes = async () => {
      const res = await customGetAPI(
        `${domain}leavetypefilter/?user_id=${userDetails?.id}${
          selectedYear?.id ? `&leave_year_id=${selectedYear.id}` : ""
        }`,
      );
      if (res.ok) {
        const data = res.responseDetails;
        setLeaveTypes(Array.isArray(data) ? data : []);
      }
    };
    if (userDetails?.id) fetchLeaveTypes();
  }, [domain, userDetails?.id, token, selectedYear]);

  // Leave requests + identity for the selected year.
  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      const res = await customGetAPI(
        `${domain}leavesummary/?user_id=${userDetails?.id}&start_date=${toApiDate(
          selectedYear?.start_date,
        )}&end_date=${toApiDate(selectedYear?.end_date)}&company_id=${requestCompanyId}`,
      );
      if (res.ok) {
        setLeaveCount(res.responseDetails?.leave_count || null);
      }
      setLoading(false);
    };
    if (selectedYear && userDetails?.id) {
      fetchSummary();
    }
  }, [domain, selectedYear, userDetails?.id, requestCompanyId]);

  const leaveRequests = useMemo(
    () => leaveCount?.leave_requests || [],
    [leaveCount],
  );

  const totals = useMemo(() => {
    const entitled = leaveTypes.reduce(
      (s, lt) => s + (Number(lt.credited_leave) || 0),
      0,
    );
    const used = leaveTypes.reduce(
      (s, lt) => s + (Number(lt.used_leave) || 0),
      0,
    );
    const today = new Date();
    const upcoming = leaveRequests.reduce((s, r) => {
      const start = parseDate(r.from_date);
      const status = (r.status || "").toLowerCase();
      const active = status === "approved" || status === "pending";
      return active && start && start > today
        ? s + (Number(r.business_days) || 0)
        : s;
    }, 0);
    return { available: entitled - used, used, upcoming };
  }, [leaveTypes, leaveRequests]);

  const expiry = useMemo(() => {
    const end = parseDate(selectedYear?.end_date);
    if (!end) return { label: "—", daysLeft: null };
    return {
      label: format(end, "dd MMM yyyy"),
      daysLeft: Math.max(differenceInCalendarDays(end, new Date()), 0),
    };
  }, [selectedYear]);

  const recentHistory = useMemo(() => leaveRequests.slice(0, 5), [leaveRequests]);

  const yearOptions = (leaveYears || []).map((y) => ({
    ...y,
    label: `${y.start_date} To ${y.end_date}`,
    filteredValue: `${y.start_date} To ${y.end_date}`,
  }));
  const selectedYearLabel = selectedYear
    ? `${selectedYear.start_date} To ${selectedYear.end_date}`
    : "";

  const displayName =
    leaveCount?.user_name ||
    [userDetails?.first_name, userDetails?.last_name].filter(Boolean).join(" ") ||
    userDetails?.first_name ||
    "—";
  const subLine = [userDetails?.employee_id, userDetails?.department_name]
    .filter(Boolean)
    .join(" • ");

  const cleanedDisplayName =
    stripEmployeeIdFromName(
      displayName,
      leaveCount?.employee_id || userDetails?.employee_id,
    ) || displayName;

  return {
    userDetails,
    selectedYear,
    setSelectedYear,
    leaveCount,
    leaveTypes,
    loading,
    leaveRequests,
    totals,
    expiry,
    recentHistory,
    yearOptions,
    selectedYearLabel,
    displayName: cleanedDisplayName,
    subLine,
  };
};
